import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Table, Card, Tabs, Tab } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import { Scrollbars } from "react-custom-scrollbars-2";
import SyntaxHighlighter from "react-syntax-highlighter";
import { github } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { getConversionDetailsView } from "../../state/reducers/conversionHistory";
import "./ConversionHistory.scss"

const ConversionDetails = () => {
  const dispatch = useDispatch();
  const { filename } = useParams();
  const { convHistoryDetails, loading, error } = useSelector((state) => state.conversionHistory);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.username && filename) {
      dispatch(getConversionDetailsView({
        username: user.username,
        filename: filename
      }));
    }
  }, [dispatch, filename]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!convHistoryDetails || convHistoryDetails.length === 0) {
    return <div>No conversion details found.</div>;
  }

  // Get the first iteration key for the default active tab
  const firstIterationKey = convHistoryDetails.length > 0 
    ? `iteration-${convHistoryDetails[0].iteration}` 
    : "";

  return (
    <div className="page-wrapper conversion-details-page">
      <Container>
        <Row>
          <Col>
            <h2 className="page-title">Conversion Details: {filename}</h2>
            
            <Tabs 
              defaultActiveKey={firstIterationKey} 
              className="conversion-details-wrapper"
            >
              {convHistoryDetails.map((iteration) => (
                <Tab 
                  key={iteration.iteration} 
                  eventKey={`iteration-${iteration.iteration}`} 
                  title={`Iteration ${iteration.iteration}`}
                  className="conversion-details-content"
                >
                  <Row>
                    <Col md={6}>
                      <Card className="mb-3">
                        <Card.Header>
                          Source Code ({iteration.source_language || "N/A"})
                        </Card.Header>
                        <Card.Body>
                          <Scrollbars autoHeight autoHeightMin={200} autoHeightMax={500}>
                            <SyntaxHighlighter 
                              style={github} 
                              language={(iteration.source_language || "").toLowerCase()}
                            >
                              {iteration.source_code || "No source code available"}
                            </SyntaxHighlighter>                                    
                          </Scrollbars>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="mb-3">
                        <Card.Header>
                          Target Code ({iteration.target_language || "N/A"})
                        </Card.Header>
                        <Card.Body>
                          <Scrollbars autoHeight autoHeightMin={200} autoHeightMax={500}>
                            <SyntaxHighlighter 
                              style={github} 
                              language={(iteration.target_language || "").toLowerCase()}
                            >
                              {iteration.target_code || "No target code available"}
                            </SyntaxHighlighter>
                          </Scrollbars>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Card className="mb-3">
                        <Card.Header>Conversion Metrics</Card.Header>
                        <Card.Body>
                          <Table striped bordered hover>
                            <tbody>
                              <tr>
                                <th>Conversion Date</th>
                                <td>
                                  {iteration.conversion_date 
                                    ? new Date(iteration.conversion_date).toLocaleString() 
                                    : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <th>Lines of Code</th>
                                <td>{iteration.lines_of_code !== undefined ? iteration.lines_of_code : "N/A"}</td>
                              </tr>
                              <tr>
                                <th>Execution Time</th>
                                <td>
                                  {iteration.execution_time !== undefined && iteration.execution_time !== null 
                                    ? iteration.execution_time.toFixed(3) 
                                    : "N/A"} seconds
                                </td>
                              </tr>
                              <tr>
                                <th>Conversion Status</th>
                                <td>{iteration.message || "N/A"}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Card className="mb-3">
                        <Card.Header>Token Usage</Card.Header>
                        <Card.Body>
                          <Table striped bordered hover>
                            <tbody>
                              <tr>
                                <th>Input Tokens</th>
                                <td>
                                  {iteration.token_usage && iteration.token_usage.input_tokens !== undefined 
                                    ? iteration.token_usage.input_tokens 
                                    : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <th>Output Tokens</th>
                                <td>
                                  {iteration.token_usage && iteration.token_usage.output_tokens !== undefined 
                                    ? iteration.token_usage.output_tokens 
                                    : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <th>Remaining Allowance</th>
                                <td>
                                  {iteration.token_usage && iteration.token_usage.remaining_allowance !== undefined 
                                    ? iteration.token_usage.remaining_allowance
                                    : "N/A"}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Card className="mb-3">
                        <Card.Header>Validation Details</Card.Header>
                        <Card.Body>
                          <Scrollbars autoHeight autoHeightMin={100} autoHeightMax={300}>
                            <p>
                              <ReactMarkdown>
                                {iteration.validation_message || "No validation message available"}
                              </ReactMarkdown>
                            </p>
                            <hr />
                            <h6>Judge Response</h6>
                            <p>
                              <ReactMarkdown>
                                {iteration.judge_response || "No judge response available"}
                              </ReactMarkdown>
                            </p>
                          </Scrollbars>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab>
              ))}
            </Tabs>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ConversionDetails;